import React, { useState, useEffect, useCallback } from 'react';
import {
  GripVertical, Trash2, Plus, Loader2, Package, ArrowUpDown,
  Tag, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import MultiSelectDialog, {
  type MultiSelectItem,
  type PagedFetch,
} from './abbinatore-v2/components/MultiSelectDialog';

interface HomepageItem {
  sku: string;
  position: number;
  addedAt: string;
  title: string;
  stock: number;
  discountPercent?: number | null;
  discountExpiry?: string | null;
}

// ── Filtri dialog ─────────────────────────────────────────────────────────
interface DialogFilters {
  category: string;
  inStock: boolean;
  withDiscount: boolean;
}

const FILTER_DEFAULT: DialogFilters = { category: '', inStock: false, withDiscount: false };

// ── Badge helpers ─────────────────────────────────────────────────────────
function StockBadge({ stock }: { stock: number }) {
  return (
    <span className={`inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded font-semibold ${
      stock > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-500'
    }`}>
      {stock > 0 ? `${stock} disp.` : 'esaurito'}
    </span>
  );
}

function DiscountBadge({ discountPercent, discountExpiry }: { discountPercent: number; discountExpiry?: string | null }) {
  const expiry = discountExpiry
    ? new Date(discountExpiry).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' })
    : null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">
      <Tag className="w-2.5 h-2.5" />
      -{discountPercent}%{expiry ? ` · scade ${expiry}` : ' · fisso'}
    </span>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────
export default function AdminHomepageProductsPage() {
  const [items, setItems] = useState<HomepageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  // Filtri del dialog
  const [dialogFilters, setDialogFilters] = useState<DialogFilters>(FILTER_DEFAULT);
  const [categories, setCategories] = useState<string[]>([]);

  // Drag & drop
  const dragItem = React.useRef<number | null>(null);
  const dragOverItem = React.useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ items: HomepageItem[]; total: number }>(
        '/api/admin/homepage-products'
      );
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error('Errore caricamento lista homepage');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica categorie per il filtro dialog
  useEffect(() => {
    api.get('/api/admin/homepage-products/categories')
      .then(r => setCategories(r.data.categories ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Fetcher per MultiSelectDialog ─────────────────────────────────────
  const fetcher = useCallback(async ({
    search, page, pageSize,
  }: { search: string; page: number; pageSize: number }): Promise<PagedFetch> => {
    if (!search.trim()) return { items: [], total: 0 };
    const params: Record<string, any> = { q: search, page, limit: pageSize };
    if (dialogFilters.category) params.category = dialogFilters.category;
    if (dialogFilters.inStock) params.in_stock = true;

    const { data } = await api.get('/api/admin/homepage-products/product-search', { params });
    let results: MultiSelectItem[] = (data.items ?? []).map((p: any) => ({
      id: p.sku,
      code: p.sku,
      brand: p.marca || '—',
      primary: p.descrizione || p.sku,
      secondary: p.categoria || undefined,
      stock: p.stock ?? undefined,
      discountPercent: p.discountPercent ?? null,
      discountExpiry: p.discountExpiry ?? null,
    }));

    // Filtro con offerta (client-side se non c'è campo in DB)
    if (dialogFilters.withDiscount) {
      results = results.filter(r => r.discountPercent != null && r.discountPercent > 0);
    }

    return { items: results, total: dialogFilters.withDiscount ? results.length : (data.total ?? results.length) };
  }, [dialogFilters]);

  const handleConfirm = useCallback(async (selected: MultiSelectItem[]) => {
    setDialogOpen(false);
    try {
      const { data } = await api.post('/api/admin/homepage-products/batch', {
        skus: selected.map(s => s.id),
      });
      const n = data.added?.length ?? 0;
      toast.success(`${n} prodott${n === 1 ? 'o aggiunto' : 'i aggiunti'} alla homepage`);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Errore aggiunta prodotti');
    }
  }, [load]);

  const removeProduct = async (sku: string) => {
    try {
      await api.delete(`/api/admin/homepage-products/${sku}`);
      toast.success('Prodotto rimosso');
      load();
    } catch {
      toast.error('Errore rimozione prodotto');
    }
  };

  // ── Drag & Drop ────────────────────────────────────────────────────────
  const handleDragStart = (index: number) => { dragItem.current = index; };
  const handleDragEnter = (index: number) => { dragOverItem.current = index; };
  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) { dragItem.current = null; return; }

    const reordered = [...items];
    const [moved] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, moved);
    dragItem.current = null;
    dragOverItem.current = null;

    const updated = reordered.map((it, i) => ({ ...it, position: i + 1 }));
    setItems(updated);
    setSavingOrder(true);
    try {
      await api.put('/api/admin/homepage-products/reorder', { skus: updated.map(it => it.sku) });
      toast.success('Ordine salvato');
    } catch {
      toast.error('Errore salvataggio ordine');
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const alreadyIn = new Set(items.map(i => i.sku));

  // ── Slot filtri dialog ─────────────────────────────────────────────────
  const extraFilters = (
    <div className="flex items-center gap-2 flex-wrap" data-testid="hp-dialog-filters">
      {/* Categoria */}
      {categories.length > 0 && (
        <select
          value={dialogFilters.category}
          onChange={e => setDialogFilters(f => ({ ...f, category: e.target.value }))}
          className="text-[11px] border border-[#d8d8d4] rounded-[6px] px-2 py-1 bg-[#fafafa] outline-none focus:border-[#2b3a8f]"
          data-testid="hp-filter-category"
        >
          <option value="">Tutte le categorie</option>
          {categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      )}
      {/* Solo disponibili */}
      <label className="flex items-center gap-1.5 text-[11px] text-[#5c5c57] cursor-pointer select-none">
        <input
          type="checkbox"
          checked={dialogFilters.inStock}
          onChange={e => setDialogFilters(f => ({ ...f, inStock: e.target.checked }))}
          data-testid="hp-filter-in-stock"
          className="rounded"
        />
        Solo disponibili
      </label>
      {/* Con offerta */}
      <label className="flex items-center gap-1.5 text-[11px] text-[#5c5c57] cursor-pointer select-none">
        <input
          type="checkbox"
          checked={dialogFilters.withDiscount}
          onChange={e => setDialogFilters(f => ({ ...f, withDiscount: e.target.checked }))}
          data-testid="hp-filter-discount"
          className="rounded"
        />
        Con offerta
      </label>
      {/* Reset filtri */}
      {(dialogFilters.category || dialogFilters.inStock || dialogFilters.withDiscount) && (
        <button
          onClick={() => setDialogFilters(FILTER_DEFAULT)}
          className="text-[10px] text-[#9b9b96] hover:text-[#5c5c57] underline"
          data-testid="hp-filter-reset"
        >
          Reset filtri
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Dialog selezione multipla */}
      <MultiSelectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Aggiungi prodotti alla homepage"
        confirmLabel={(n) => n === 0 ? 'Seleziona prodotti' : `Aggiungi ${n} prodott${n === 1 ? 'o' : 'i'}`}
        fetcher={fetcher}
        alreadyIn={alreadyIn}
        alreadyInLabel="già in homepage"
        searchPlaceholder="cerca per SKU, descrizione o marca…"
        onConfirm={handleConfirm}
        testIdPrefix="hp-dialog"
        extraFilters={extraFilters}
      />

      {/* Pagina */}
      <div className="p-6" data-testid="admin-homepage-products-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Prodotti Homepage</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {total} prodotti · 15 per pagina · trascina per riordinare
              {savingOrder && (
                <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" /> salvataggio…
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,35%)] transition-colors flex-shrink-0"
            data-testid="hp-open-dialog-btn"
          >
            <Plus className="w-4 h-4" />
            Aggiungi prodotti
          </button>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border bg-white p-14 text-center" data-testid="hp-empty-state">
            <Package className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium text-muted-foreground">Nessun prodotto in homepage</p>
            <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs mx-auto">
              Usa il pulsante "Aggiungi prodotti" per selezionare i prodotti da mostrare in homepage.
            </p>
            <button
              onClick={() => setDialogOpen(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,35%)] transition-colors mx-auto"
              data-testid="hp-empty-add-btn"
            >
              <Plus className="w-4 h-4" />
              Aggiungi prodotti
            </button>
          </div>
        ) : (
          <div className="bg-white border rounded-xl divide-y overflow-hidden" data-testid="hp-products-list">
            {/* Intestazione colonne */}
            <div className="flex items-center gap-3 px-3 py-2 bg-secondary/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <div className="w-5" />
              <span className="w-6 text-center">#</span>
              <span className="w-28">SKU</span>
              <span className="flex-1">Descrizione</span>
              <span className="w-24 text-center">Stock</span>
              <span className="w-40">Offerta</span>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground ml-2">
                <ArrowUpDown className="w-3 h-3" /> trascina
              </div>
              <div className="w-7" />
            </div>

            {items.map((item, index) => (
              <div
                key={item.sku}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/20 cursor-grab active:cursor-grabbing transition-colors"
                data-testid={`hp-item-${item.sku}`}
              >
                <GripVertical className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                <span className="w-6 text-center text-xs text-muted-foreground font-mono">{index + 1}</span>
                <span className="w-28 text-xs font-mono truncate flex-shrink-0">{item.sku}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" title={item.title}>{item.title || item.sku}</p>
                </div>
                <div className="w-24 flex justify-center flex-shrink-0">
                  <StockBadge stock={item.stock} />
                </div>
                <div className="w-40 flex-shrink-0">
                  {item.discountPercent != null && item.discountPercent > 0
                    ? <DiscountBadge discountPercent={item.discountPercent} discountExpiry={item.discountExpiry} />
                    : <span className="text-xs text-muted-foreground/50">—</span>
                  }
                </div>
                <div className="w-2 ml-2" />
                <button
                  onClick={() => removeProduct(item.sku)}
                  title="Rimuovi dalla homepage"
                  className="flex-shrink-0 w-7 h-7 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors"
                  data-testid={`hp-remove-${item.sku}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
